import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import PaginatedTable from '../../components/PaginatedTable';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';

interface IPessoa {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  tipo: string;
  funcao: string;
  empresa_id: number;
}

const PersonListPage: React.FC = () => {
  const [pessoas, setPessoas] = useState<IPessoa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();

  useEffect(() => {
    const fetchPessoas = async () => {
      setLoading(true);
      try {
        const response = await api.getPessoas(empresaId ?? undefined);
        if (response.success && response.data) {
          setPessoas(response.data as IPessoa[]);
        } else {
          setError('Erro ao carregar pessoas.');
        }
      } catch {
        setError('Falha de conexão ao buscar pessoas.');
      } finally {
        setLoading(false);
      }
    };
    fetchPessoas();
  }, [empresaId]);

  const handleDelete = async (item: IPessoa) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${item.nome}"?`)) return;
    try {
      const response = await api.deletePessoa(item.id);
      if (response.success) {
        setPessoas(prev => prev.filter(p => p.id !== item.id));
      } else {
        notify.error('Falha ao excluir.');
      }
    } catch {
      notify.error('Erro de conexão ao excluir.');
    }
  };

  const handleEdit = (item: IPessoa) => {
    navigate(`/app/pessoas/${item.id}`);
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" /> <span>Carregando pessoas...</span>
      </div>
    );
  }

  const columns: { key: keyof IPessoa; title: string }[] = [
    { key: 'id', title: 'ID' },
    { key: 'nome', title: 'Nome' },
    { key: 'tipo', title: 'Tipo' },
    { key: 'funcao', title: 'Função' },
    { key: 'email', title: 'E-mail' },
    { key: 'telefone', title: 'Telefone' },
  ];

  return (
    <>
      <div className="mb-4">
        <h2>Listagem de Pessoas</h2>
        <p className="text-muted">Visualize, edite ou exclua as pessoas cadastradas.</p>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <PaginatedTable<IPessoa>
        data={pessoas}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </>
  );
};

export default PersonListPage;
